import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';

import './CollegeExchangeSteps.scss';

import CollegeExchangeFundListActionBarLink from '../../Exchange/CollegeExchangeFundListActionBarLink/CollegeExchangeFundListActionBarLink';
import ExchangeFromForm from '../../Exchange/ExchangeFromForm/ExchangeFromForm';
import ExchangeFromSummary from '../../Exchange/ExchangeFromSummary/ExchangeFromSummary';
import ExchangeReview from '../../Exchange/ExchangeReview/ExchangeReview';
import ExchangeToForm from '../../Exchange/ExchangeToForm/ExchangeToForm';
import ExchangeToSummary from '../../Exchange/ExchangeToSummary/ExchangeToSummary';
import ProgressStep from '../../ProgressStep/ProgressStep';
import { moduleActions } from '../../../redux/modules';
import {
  isCustomerWebViewEnabled,
  isSalesforceRep,
} from '../../../redux/modules/authentication/authentication.module.selectors';
import {
  getExchangeContributionYear,
  getExchangeFederalWithholdingApplied,
  getExchangeFederalWithholdingPercent,
  getExchangeFromIsValid,
  getExchangeFromSummaryFundList,
  getExchangeToIsValid,
  getExchangeToSummaryFundList,
  getExchangeType,
} from '../../../redux/modules/exchangeTransaction/exchangeTransaction.selectors';
import { CANCEL_TRADE_CONFIRMATION_MODAL } from '../../../redux/modules/modals/modals.module';
import {
  getHasMixedCollegeTypes,
  getOnlyCollegeType,
} from '../../../redux/modules/productTypes/productType.selectors';
import { ADD_FUND_MODAL_PROPS } from '../../../settings/settings';
import AppTitle from '../../Shared/AppTitle/AppTitle';
import ErrorSlideIn from '../../Shared/ErrorSlideIn/ErrorSlideIn';
import { scrollElementIntoFullView } from '../../../utils/dom-utils';
import { hasValue } from '../../../utils/utils';

const pickSelectedRegistrations = (registrations, selectedFundList) => {
  if (!hasValue(selectedFundList) || !hasValue(registrations)) {
    return [];
  }

  const firstSelectedFund = selectedFundList[0]; // In MF Exchange only the first fund is used to generate the registration
  const matchingKey = `${
    firstSelectedFund.agreementID
  }-${firstSelectedFund.agreementNumber.replace(/-\d/, '')}`;

  return registrations.find((reg) =>
    reg.modelAgreementUniqueIDs.some((id) => id.endsWith(matchingKey))
  );
};

const CollegeExchangeSteps = ({
  actions: {
    openModal,
    selectExchangeToFunds,
    setProgress,
    submitExchange,
    setRegistrationTypes,
    validateExchange,
    resetTransaction,
  },
  isSalesforceRep,
  customerWebViewEnabled,
  currentStep,
  exchangeContributionYear,
  exchangeFederalWithholdingApplied,
  exchangeFederalWithholdingPercent,
  exchangeFromIsValid,
  exchangeFromSummaryFundList,
  exchangeToIsValid,
  exchangeToSummaryFundList,
  exchangeType,
  hasMixedCollegeTypes,
  onlyCollegeType,
  isLoading,
  isValid,
  isValidated,
  isValidating,
  userRegistrationTypeInfo,
  validationMessages,
}) => {
  const fundListActionBarModalProps =
    ADD_FUND_MODAL_PROPS[
      hasMixedCollegeTypes ? 'MIXED_COLLEGE' : `${onlyCollegeType}_COLLEGE`
    ];
  return (
    <form onSubmit={(e) => e.preventDefault()} className="animated fadeInUp">
      <AppTitle
        title="Exchange Within Your 529 Plan"
        subtitle="Our three-step process makes it easy to move investments between existing or new portfolios."
      />
      <ProgressStep
        title="Exchange from"
        className="exchange-from-step step1"
        step={1}
        active={currentStep === 1}
        complete={currentStep > 1}
        expanded
        nextButtonLabel={
          <span>
            <b>NEXT: </b>Select{' '}
            <span className="college-exchange-steps-button-exchange-to">
              Exchange To
            </span>{' '}
            Portfolio(s)
          </span>
        }
        nextButtonDisabled={!exchangeFromIsValid}
        onNextClick={() => {
          selectExchangeToFunds(exchangeFromSummaryFundList);
          setRegistrationTypes(
            pickSelectedRegistrations(
              userRegistrationTypeInfo.registrationTypes,
              exchangeFromSummaryFundList
            )
          );
          setProgress(2);
        }}
        showCancelLink={!(!customerWebViewEnabled && isSalesforceRep)}
        onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
      >
        {currentStep === 1 ? <ExchangeFromForm /> : <ExchangeFromSummary />}
      </ProgressStep>

      <ProgressStep
        title="Exchange to"
        className="exchange-to-step step2"
        step={2}
        active={currentStep === 2}
        complete={currentStep > 2}
        expanded
        nextButtonClassName="e2e-step2-verify-submit"
        nextButtonLabel={
          <span>
            <b>NEXT:</b> Review & Submit
          </span>
        }
        nextButtonDisabled={!exchangeToIsValid || isValidating}
        nextButtonLoading={isValidating}
        onNextClick={() =>
          validateExchange(
            exchangeContributionYear,
            exchangeFederalWithholdingApplied,
            exchangeFederalWithholdingPercent,
            exchangeFromSummaryFundList,
            exchangeToSummaryFundList,
            exchangeType
          ).then(({ body = {} }) => {
            if (body.valid) {
              setProgress(3);
            }
          })
        }
        showCancelLink={!(!customerWebViewEnabled && isSalesforceRep)}
        onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
      >
        {currentStep === 2 ? (
          <ExchangeToForm
            fundListActionBarLink={CollegeExchangeFundListActionBarLink}
            fundListActionBarModalProps={fundListActionBarModalProps}
            fromFunds={exchangeFromSummaryFundList}
            selectedFund={exchangeFromSummaryFundList[0]}
            currentStep={currentStep}
          />
        ) : (
          <ExchangeToSummary />
        )}
      </ProgressStep>

      <ErrorSlideIn msg={validationMessages} show={isValidated && !isValid} />

      <ProgressStep
        title={
          <span>
            Review <span className="visible-xs-inline">& </span>
            <span className="hidden-xs">and </span>submit{' '}
            <span className="hidden-xs"> your exchange</span>
          </span>
        }
        className="review-step step3"
        step={3}
        active={currentStep === 3}
        expanded={currentStep === 3}
        nextButtonLabel="SUBMIT"
        nextButtonDisabled={customerWebViewEnabled || isLoading}
        onNextClick={() => {
          scrollElementIntoFullView('college-page-container');
          submitExchange(
            exchangeContributionYear,
            exchangeFederalWithholdingApplied,
            exchangeFederalWithholdingPercent,
            exchangeFromSummaryFundList,
            exchangeToSummaryFundList,
            exchangeType
          );
          resetTransaction();
        }}
        showCancelLink={!(!customerWebViewEnabled && isSalesforceRep)}
        onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
      >
        {currentStep === 3 ? <ExchangeReview /> : null}
      </ProgressStep>
    </form>
  );
};

const mapStateToProps = (state) => {
  const {
    executeExchange: {
      isLoading,
      isValid,
      isValidated,
      isValidating,
      validationMessages,
    },
    progress: { currentStep },
    userRegistrationTypeInfo,
  } = state;
  return {
    currentStep,
    isSalesforceRep: isSalesforceRep(state),
    customerWebViewEnabled: isCustomerWebViewEnabled(state),
    exchangeContributionYear: getExchangeContributionYear(state),
    exchangeFederalWithholdingApplied:
      getExchangeFederalWithholdingApplied(state),
    exchangeFederalWithholdingPercent:
      getExchangeFederalWithholdingPercent(state),
    exchangeFromIsValid: getExchangeFromIsValid(state),
    exchangeFromSummaryFundList: getExchangeFromSummaryFundList(state),
    exchangeToIsValid: getExchangeToIsValid(state),
    exchangeToSummaryFundList: getExchangeToSummaryFundList(state),
    exchangeType: getExchangeType(state),
    hasMixedCollegeTypes: getHasMixedCollegeTypes(state),
    isLoading,
    isValid,
    isValidated,
    isValidating,
    onlyCollegeType: getOnlyCollegeType(state),
    userRegistrationTypeInfo,
    validationMessages,
  };
};

const mapDispatchToProps = (dispatch) => ({
  actions: {
    ...bindActionCreators(moduleActions, dispatch),
    setProgress: (step) => {
      scrollElementIntoFullView(`step${step}`);
      dispatch(moduleActions.setProgress(step));
    },
    selectExchangeToFunds: (exchangeFromSummaryFundList) => {
      if (hasValue(exchangeFromSummaryFundList)) {
        dispatch(
          moduleActions.searchExchangeToExisting(exchangeFromSummaryFundList[0])
        );
      }
    },
  },
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  CollegeExchangeSteps
);
